/**
 * "Did you mean …@gmail.com?" for the lead forms' email fields.
 *
 * About 1 in 220 leads arrives with a misspelled webmail domain (`gamil.com`,
 * `gmail.con`, `hotmial.com`), and several of those domains accept mail, so
 * what we send goes to a stranger instead of bouncing. Both form surfaces call
 * this on blur, the Svelte `Text` input and the legacy `form-field-input.ts`,
 * and show the answer as a hint the visitor may click or ignore. It never
 * blocks a submit: a near-miss can be a real domain.
 */

type Provider = {
  /** The label before the first dot, as the visitor should have typed it. */
  name: string
  /** What follows `name.` on a real address; the first is the one we suggest. */
  suffixes: string[]
  /** No country domains exist, so any other suffix is a typo (`gmail.ca`, `gmail.com.pk`). */
  only?: boolean
  /**
   * Also match a misspelled `name`. Off where one edit lands on a real domain
   * someone may own (`aol` → `aon`, `sol`; `live` → `life`; `ymail` → `email`).
   */
  fuzzy?: boolean
}

// Ordered by how often each appears on tbl_leads, so a tie goes to the commoner one.
const PROVIDERS: Provider[] = [
  { name: "gmail", suffixes: ["com"], only: true, fuzzy: true },
  {
    name: "yahoo",
    suffixes: [
      "com",
      "ca",
      "co.uk",
      "co.in",
      "in",
      "fr",
      "de",
      "es",
      "it",
      "ie",
      "gr",
      "ro",
      "com.au",
      "com.br",
      "com.mx",
      "com.ar",
      "com.hk",
      "com.tw",
      "com.my",
      "com.sg",
      "com.ph",
      "com.vn",
      "co.jp",
      "co.id",
      "co.nz",
      "co.za",
    ],
    fuzzy: true,
  },
  {
    name: "hotmail",
    suffixes: [
      "com",
      "co.uk",
      "ca",
      "fr",
      "de",
      "es",
      "it",
      "nl",
      "be",
      "se",
      "no",
      "co.nz",
      "co.za",
      "com.au",
      "com.br",
      "com.ar",
      "com.tw",
    ],
    fuzzy: true,
  },
  {
    name: "outlook",
    suffixes: [
      "com",
      "co.uk",
      "fr",
      "de",
      "es",
      "it",
      "ie",
      "dk",
      "cz",
      "sg",
      "my",
      "sa",
      "in",
      "jp",
      "com.au",
      "com.br",
      "com.ar",
      "com.vn",
    ],
    fuzzy: true,
  },
  { name: "aol", suffixes: ["com", "co.uk", "de", "fr"] },
  { name: "icloud", suffixes: ["com"], only: true, fuzzy: true },
  { name: "comcast", suffixes: ["net"], fuzzy: true },
  { name: "live", suffixes: ["com", "ca", "co.uk", "fr", "nl", "de", "it", "ie", "com.au", "com.mx"] },
  { name: "msn", suffixes: ["com"] },
  { name: "att", suffixes: ["net"] },
  { name: "sbcglobal", suffixes: ["net"], fuzzy: true },
  { name: "bellsouth", suffixes: ["net"], fuzzy: true },
  { name: "ymail", suffixes: ["com"], only: true },
  { name: "protonmail", suffixes: ["com", "ch"], fuzzy: true },
  { name: "verizon", suffixes: ["net", "com"], fuzzy: true },
  { name: "googlemail", suffixes: ["com"], only: true, fuzzy: true },
]

/** Real domains one edit from a fuzzy provider's name. */
const REAL = new Set(["email.com", "mail.com", "cloud.com", "wahoo.com"])

/**
 * The address the visitor most likely meant, or `null` when the domain looks
 * right or is too far from any webmail provider to guess.
 */
export function suggestEmail(input: string): string | null {
  const address = input.trim()
  const at = address.lastIndexOf("@")
  if (at < 1) return null

  const local = address.slice(0, at)
  const domain = address
    .slice(at + 1)
    .toLowerCase()
    .replace(/\.+$/, "")
  if (domain === "" || REAL.has(domain)) return null

  const fixed = suggestDomain(domain)
  return fixed == null || fixed === domain ? null : `${local}@${fixed}`
}

function suggestDomain(domain: string): string | null {
  const dot = domain.indexOf(".")
  if (dot === -1) return suggestDotless(domain)

  const name = domain.slice(0, dot)
  const suffix = domain.slice(dot + 1)

  const exact = PROVIDERS.find((p) => p.name === name)
  if (exact) {
    if (exact.suffixes.includes(suffix)) return null
    return fixSuffix(exact, suffix, true)
  }

  let best: { provider: Provider; distance: number } | undefined
  for (const provider of PROVIDERS) {
    if (!provider.fuzzy) continue
    const distance = editDistance(name, provider.name)
    if (distance > maxDistance(provider.name)) continue
    if (best == null || distance < best.distance) best = { provider, distance }
  }
  if (best == null) return null
  if (best.provider.suffixes.includes(suffix)) return `${best.provider.name}.${suffix}`
  return fixSuffix(best.provider, suffix, false)
}

// `gmail` or `gmailcom`: the dot never got typed.
function suggestDotless(domain: string): string | null {
  const provider = PROVIDERS.find((p) => domain === p.name || domain === p.name + p.suffixes[0])
  return provider ? canonical(provider) : null
}

/**
 * A suffix that is one slip from the provider's main one (`con`, `co`, `comm`,
 * `com1`), or that repeats it (`com.com`), is a typo. So is any suffix on a
 * provider with no country domains, but only when the name itself was right:
 * `gnail.co.uk` is too far from anything to guess.
 */
function fixSuffix(provider: Provider, suffix: string, nameWasExact: boolean): string | null {
  const [main] = provider.suffixes
  const slipped = editDistance(suffix, main) <= 1
  const trailing = suffix.startsWith(main) && suffix[main.length] !== "."
  const repeated = suffix === `${main}.${main}`
  if (slipped || trailing || repeated) return canonical(provider)
  if (provider.only && nameWasExact) return canonical(provider)
  return null
}

function canonical(provider: Provider) {
  return `${provider.name}.${provider.suffixes[0]}`
}

// One slip for most names; two once a name is long enough that two typos
// still leave it unmistakable (`sbcgoobal`, `protnmail`).
function maxDistance(name: string) {
  return name.length >= 9 ? 2 : 1
}

/** Optimal string alignment distance: Levenshtein plus adjacent swaps (`gamil`), each costing one. */
export function editDistance(a: string, b: string): number {
  const rows = Array.from({ length: a.length + 1 }, (_, i) =>
    Array.from({ length: b.length + 1 }, (_, j) =>
      i === 0 ? j
      : j === 0 ? i
      : 0,
    ),
  )

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      rows[i][j] = Math.min(rows[i - 1][j] + 1, rows[i][j - 1] + 1, rows[i - 1][j - 1] + cost)
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        rows[i][j] = Math.min(rows[i][j], rows[i - 2][j - 2] + 1)
      }
    }
  }
  return rows[a.length][b.length]
}
